/**
 * Elements placed freely on a page.
 *
 * Everything this kind is: how it is made, how it is read back, what it looks
 * like on the page, and how it is edited. Nothing outside this folder needs to
 * know the kind exists, with one exception: the sheet draws the elements over
 * the page (see `FreeLayer.vue`), because they belong to the page and not to
 * the place in the flow where the section stands.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **Why a section, and why it takes no room.**
 *
 * A designed page needs its elements to belong to a page, and a page is not a
 * thing this document holds: pages are worked out from the sections when the
 * report is drawn. A section is the one thing that has a place in the report,
 * can be moved from page to page in the outline, duplicated, deleted and
 * undone. So the elements ride on a section, and the section takes no room in
 * the flow so the page prints exactly as it did before they were placed.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { readFreeElement, type FreeElement } from '@/domain/free'
import { readArray } from '@/domain/json'
import { type LayerSection } from '@/domain/section'
import LayerBlock from '@/sections/layer/LayerBlock.vue'
import LayerInspector from '@/sections/layer/LayerInspector.vue'
import type { SectionDefinition } from '@/sections/types'

/**
 * The colours offered for a fill, an ink or a stroke, besides the document's
 * own accent. Enough to design a page in the house's colours; anything else
 * is a colour well away.
 */
export const FREE_COLOURS = [
  { id: 'ink', colour: '#16202c' },
  { id: 'white', colour: '#ffffff' },
  { id: 'grey', colour: '#eef1f4' },
  { id: 'navy', colour: '#1c3f6e' },
  { id: 'teal', colour: '#0f6b68' },
  { id: 'sand', colour: '#f3ead9' },
] as const

export const layerSection: SectionDefinition<'layer', LayerSection> = {
  kind: 'layer',
  band: 'structure',

  // Two squares, one laid over the other: things placed where they were put.
  icon: ['M2.5 2.5h7v7h-7z', 'M6.5 6.5h7v7h-7z'],

  create: (id, title) => ({
    id,
    kind: 'layer',
    title,
    elements: [],
  }),

  read: ({ id, title, stored }) => ({
    id,
    kind: 'layer',
    title,
    elements: readArray(stored, 'elements')
      .map(readFreeElement)
      .filter((element): element is FreeElement => element !== null),
  }),

  block: LayerBlock,
  inspector: LayerInspector,
}
